/**
 * Agent Form Page - Create or edit an agent.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  Alert,
  Skeleton,
  Paper,
  Chip,
  CircularProgress,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import {
  Save,
  Cloud,
  Warning,
  CheckCircle,
  Tune,
  ContentCopy,
} from '@mui/icons-material';
import PsychologyIcon from '@mui/icons-material/Psychology';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import ButtonTabs, { type ButtonTab } from '../components/ButtonTabs';
import DetailHeader from '../components/DetailHeader';
import TenantResourceCopyDialog from '../components/TenantResourceCopyDialog';
import type { AIAgentProvider, Agent, AgentDirection } from '../types';
import usePageTitle from '../hooks/usePageTitle';
import { useAuth } from '../contexts/AuthContext';
import { isProtectedSystemVariableKey, substituteVariables } from '../utils/variableExtractor';
import { useDynamicVariables, type DynamicVariablesContainer } from '../hooks/useDynamicVariables';
import { extractAgentDynamicVariableValues, omitAgentDynamicVariables } from '../utils/agentDynamicVariables';

interface ProviderAgentOption {
  id: string;
  name: string;
}

/**
 * Extract the canonical provider agent id from a display-oriented reference.
 *
 * Existing rows may still temporarily arrive as `Name (agent_xxx)`. The form
 * must persist only the upstream id.
 */
function extractProviderAgentId(value: string): string {
  const normalized = String(value || '').trim();
  if (!normalized) {
    return '';
  }
  const match = normalized.match(/\(([^)]+)\)\s*$/);
  if (match?.[1]) {
    return match[1].trim();
  }
  return normalized;
}

type ExternalAgentConfig = DynamicVariablesContainer & Record<string, unknown>;
type AgentFormConfig = ExternalAgentConfig;
type AgentTab = 'basic' | 'dynamicVariables';

export default function AgentFormPage() {
  const { t } = useTranslation(['pages', 'common']);
  const navigate = useNavigate();
  const { user, currentTenant, isSuperuser } = useAuth();
  const { id } = useParams<{ id: string }>();
  const isEditing = Boolean(id);

  // Data
  const [providers, setProviders] = useState<AIAgentProvider[]>([]);
  const [providerAgents, setProviderAgents] = useState<ProviderAgentOption[]>([]);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    aiAgentProviderId: '',
    providerAgentId: '',
    direction: 'outbound' as AgentDirection,
    transferTargetRequired: false,
    appointmentCalendarRequired: false,
    requiresPreCallScript: false,
  });
  const [providerAgentInputValue, setProviderAgentInputValue] = useState('');
  const [externalConfig, setExternalConfig] = useState<ExternalAgentConfig>({});

  const selectedProvider = providers.find(p => String(p.id) === formData.aiAgentProviderId);
  const inferredAgentType = selectedProvider?.providerType || '';
  const activeConfig: AgentFormConfig = externalConfig;

  const handleActiveConfigChange = useCallback(
    (updatedConfig: AgentFormConfig) => {
      setExternalConfig((prev) => ({ ...prev, ...updatedConfig }));
    },
    [],
  );

  const {
    detectedVariables,
    missingVariables,
    dynamicVariables,
    updateDynamicVariable,
  } = useDynamicVariables({
    config: activeConfig,
    onChange: handleActiveConfigChange,
    sourceTexts: [],
  });
  const [dynamicVariablesMarkedForRemoval, setDynamicVariablesMarkedForRemoval] = useState<Record<string, boolean>>({});
  const [newDynamicVariableKey, setNewDynamicVariableKey] = useState('');
  const [newDynamicVariableValue, setNewDynamicVariableValue] = useState('');
  const normalizedNewVariableKey = newDynamicVariableKey.trim();
  const isNewKeyDuplicate = Boolean(
    normalizedNewVariableKey && Object.prototype.hasOwnProperty.call(dynamicVariables, normalizedNewVariableKey),
  );
  const markedForRemovalKeys = useMemo(
    () => Object.keys(dynamicVariablesMarkedForRemoval).filter(
      (key) => Boolean(dynamicVariablesMarkedForRemoval[key]) && Object.prototype.hasOwnProperty.call(dynamicVariables, key),
    ),
    [dynamicVariables, dynamicVariablesMarkedForRemoval],
  );
  const markedForRemovalKeySet = useMemo(() => new Set(markedForRemovalKeys), [markedForRemovalKeys]);
  const effectiveDynamicVariables = useMemo(
    () => omitAgentDynamicVariables(dynamicVariables, markedForRemovalKeySet),
    [markedForRemovalKeySet, dynamicVariables],
  );
  const effectiveDynamicVariableValues = useMemo(
    () => extractAgentDynamicVariableValues(effectiveDynamicVariables),
    [effectiveDynamicVariables],
  );
  const protectedSystemVariableKeys = useMemo(
    () =>
      Object.keys(dynamicVariables).filter(
        (key) => isProtectedSystemVariableKey(key) && !markedForRemovalKeySet.has(key),
      ),
    [markedForRemovalKeySet, dynamicVariables],
  );
  const hasProtectedSystemVariables = protectedSystemVariableKeys.length > 0;
  const isNewKeyProtected = isProtectedSystemVariableKey(newDynamicVariableKey);
  const protectedSystemVariablesErrorMessage = hasProtectedSystemVariables
    ? t('pages:agents.errors.protectedSystemVariables', { keys: protectedSystemVariableKeys.join(', ') })
    : null;
  const handleAddDynamicVariable = useCallback(() => {
    if (!normalizedNewVariableKey) return;
    if (isProtectedSystemVariableKey(normalizedNewVariableKey)) {
      setError(t('pages:agents.errors.protectedSystemVariableSingle', { key: normalizedNewVariableKey }));
      return;
    }
    if (Object.prototype.hasOwnProperty.call(dynamicVariables, normalizedNewVariableKey)) {
      setError(t('pages:agents.dynamicVariables.duplicateKeyHelper'));
      return;
    }
    handleActiveConfigChange({
      ...activeConfig,
      dynamicVariables: {
        ...dynamicVariables,
        [normalizedNewVariableKey]: {
          value: newDynamicVariableValue,
        },
      },
    });
    setNewDynamicVariableKey('');
    setNewDynamicVariableValue('');
  }, [
    activeConfig,
    handleActiveConfigChange,
    normalizedNewVariableKey,
    newDynamicVariableValue,
    t,
    dynamicVariables,
  ]);
  const toggleDynamicVariableMarkedForRemoval = useCallback((name: string, checked: boolean) => {
    setDynamicVariablesMarkedForRemoval((prev) => {
      if (!checked) {
        if (!prev[name]) {
          return prev;
        }
        const next = { ...prev };
        delete next[name];
        return next;
      }
      if (prev[name]) {
        return prev;
      }
      return {
        ...prev,
        [name]: true,
      };
    });
  }, []);

  // UI state
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [loadingProviderAgents, setLoadingProviderAgents] = useState(false);
  const [providerAgentsError, setProviderAgentsError] = useState<string | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [agent, setAgent] = useState<Agent | null>(null);
  const [copyDialogOpen, setCopyDialogOpen] = useState(false);
  const [copySubmitting, setCopySubmitting] = useState(false);
  const [copyError, setCopyError] = useState<string | null>(null);

  const agentDescriptor = agent?.name || formData.name;
  usePageTitle(t('pages:agents.title'), agentDescriptor);
  const canCopyResource = Boolean(
    isEditing
      && agent?.id
      && isSuperuser
      && user?.tenantId
      && currentTenant?.id
      && currentTenant.id === user.tenantId,
  );

  const agentTabs = useMemo<ButtonTab[]>(() => {
    const tabs: ButtonTab[] = [
      {
        id: 'basic',
        label: t('pages:agents.basicInfo'),
        tabProps: {
          icon: <PsychologyIcon sx={{ fontSize: 18 }} />,
          iconPosition: 'start',
        },
      },
    ];

    tabs.push({
      id: 'dynamicVariables',
      label: t('pages:agents.dynamicVariables.title'),
      tabProps: {
        icon: <Tune sx={{ fontSize: 18 }} />,
        iconPosition: 'start',
      },
    });

    return tabs;
  }, [t]);

  const tabFromUrl = searchParams.get('tab') as AgentTab | null;
  const normalizedTab = agentTabs.some((tab) => tab.id === tabFromUrl)
    ? (tabFromUrl as AgentTab)
    : 'basic';

  useEffect(() => {
    const params = new URLSearchParams(searchParams);
    const currentTab = params.get('tab');
    if (normalizedTab === 'basic' && currentTab) {
      params.delete('tab');
      setSearchParams(params, { replace: true });
      return;
    }
    if (normalizedTab !== 'basic' && currentTab !== normalizedTab) {
      params.set('tab', normalizedTab);
      setSearchParams(params, { replace: true });
    }
  }, [normalizedTab, searchParams, setSearchParams]);

  const selectTab = useCallback(
    (tab: AgentTab) => {
      const params = new URLSearchParams(searchParams);
      if (tab === 'basic') {
        params.delete('tab');
      } else {
        params.set('tab', tab);
      }
      setSearchParams(params, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const fetchInitialDataRef = useRef<() => Promise<void>>(async () => undefined);
  const fetchProviderAgentsRef = useRef<(providerId: string) => Promise<void>>(
    async () => undefined,
  );

  useEffect(() => {
    void fetchInitialDataRef.current();
  }, [id]);

  useEffect(() => {
    if (selectedProvider && formData.aiAgentProviderId) {
      void fetchProviderAgentsRef.current(formData.aiAgentProviderId);
    } else {
      setProviderAgents([]);
      setProviderAgentsError(null);
    }
  }, [selectedProvider, formData.aiAgentProviderId]);

  useEffect(() => {
    setDynamicVariablesMarkedForRemoval((prev) => {
      const nextEntries = Object.entries(prev).filter(
        ([key, marked]) => Boolean(marked) && Object.prototype.hasOwnProperty.call(dynamicVariables, key),
      );
      if (nextEntries.length === Object.keys(prev).length) {
        return prev;
      }
      return Object.fromEntries(nextEntries);
    });
  }, [dynamicVariables]);

  useEffect(() => {
    const canonicalProviderAgentId = extractProviderAgentId(formData.providerAgentId);
    const selectedProviderAgent = providerAgents.find(
      (providerAgent) => providerAgent.id === canonicalProviderAgentId,
    );
    if (selectedProviderAgent) {
      setProviderAgentInputValue(selectedProviderAgent.name);
      return;
    }
    setProviderAgentInputValue(formData.providerAgentId);
  }, [formData.providerAgentId, providerAgents]);

  const fetchInitialData = async () => {
    try {
      setLoading(true);
      setAgent(null);

      // Fetch providers that support agents
      const providersResponse = await api.get('/api/ai-agents-providers/');
      const providersData = providersResponse.data.results ?? providersResponse.data;
      setProviders(Array.isArray(providersData) ? providersData : []);

      // If editing, fetch the agent data
      if (id) {
        const agentResponse = await api.get(`/api/agents/${id}/`);
        const agent: Agent = agentResponse.data;
        setAgent(agent);

        setFormData({
          name: agent.name,
          description: agent.description || '',
          aiAgentProviderId: agent.aiAgentProviderId?.toString() || '',
          providerAgentId: extractProviderAgentId(agent.providerAgentId || ''),
          direction: agent.direction,
          transferTargetRequired: Boolean(agent.transferTargetRequired),
          appointmentCalendarRequired: Boolean(agent.appointmentCalendarRequired),
          requiresPreCallScript: Boolean(agent.requiresPreCallScript),
        });
        setProviderAgentInputValue(agent.providerAgentId || '');

        const config = (agent.config && typeof agent.config === 'object') ? agent.config : {};
        setExternalConfig(config as ExternalAgentConfig);
        setDynamicVariablesMarkedForRemoval({});
      }
    } catch (err: unknown) {
      const error = err as { response?: { data?: { detail?: string } } };
      setError(error?.response?.data?.detail || t('pages:agents.errors.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const fetchProviderAgents = async (aiAgentProviderId: string) => {
    setLoadingProviderAgents(true);
    setProviderAgentsError(null);

    try {
      const response = await api.get(`/api/ai-agents-providers/${aiAgentProviderId}/agents/`);
      setProviderAgents(response.data.agents || []);
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: string } } };
      setProviderAgentsError(error?.response?.data?.error || t('pages:agents.errors.loadProviderAgentsFailed'));
      setProviderAgents([]);
    } finally {
      setLoadingProviderAgents(false);
    }
  };

  fetchInitialDataRef.current = fetchInitialData;
  fetchProviderAgentsRef.current = fetchProviderAgents;

  const handleSubmit = async () => {
    if (protectedSystemVariablesErrorMessage) {
      setError(protectedSystemVariablesErrorMessage);
      return;
    }
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const submittedDynamicVariables = omitAgentDynamicVariables(dynamicVariables, markedForRemovalKeySet);
      const submittedConfig: ExternalAgentConfig = {
        ...externalConfig,
      };
      if (
        Object.keys(submittedDynamicVariables).length > 0
        || Object.prototype.hasOwnProperty.call(externalConfig, 'dynamicVariables')
        || markedForRemovalKeys.length > 0
      ) {
        submittedConfig.dynamicVariables = submittedDynamicVariables;
      } else {
        delete submittedConfig.dynamicVariables;
      }

      const payload: Record<string, unknown> = {
        name: formData.name,
        description: formData.description,
        // Use inferred agent type from provider
        agent_type: inferredAgentType,
        direction: formData.direction,
        transfer_target_required: formData.transferTargetRequired,
        appointment_calendar_required: formData.appointmentCalendarRequired,
        requires_pre_call_script: formData.requiresPreCallScript,
      };

      // All agent types require a provider - send id (UUID), not numeric id
      payload.ai_agent_provider_id = selectedProvider?.id || null;

      // Add type-specific fields
      payload.provider_agent_id = extractProviderAgentId(formData.providerAgentId);
      payload.config = submittedConfig;

      if (isEditing) {
        const response = await api.patch(`/api/agents/${id}/`, payload);
        const updatedAgent = response?.data && typeof response.data === 'object'
          ? (response.data as Agent)
          : null;
        if (updatedAgent) {
          setAgent(updatedAgent);
        }
        const responseConfig = updatedAgent?.config && typeof updatedAgent.config === 'object'
          ? (updatedAgent.config as ExternalAgentConfig)
          : submittedConfig;
        setExternalConfig(responseConfig);
        setDynamicVariablesMarkedForRemoval({});
        setSuccess(t('pages:agents.updateSuccess'));
      } else {
        await api.post('/api/agents/', payload);
        setDynamicVariablesMarkedForRemoval({});
        setSuccess(t('pages:agents.createSuccess'));
        // Navigate to list after creating
        setTimeout(() => navigate('/agents'), 1500);
      }
    } catch (err: unknown) {
      const error = err as { response?: { data?: { detail?: string; error?: string } } };
      setError(error?.response?.data?.detail || error?.response?.data?.error || t('pages:agents.errors.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleCopyToTenant = useCallback(async (targetTenantId: string) => {
    if (!id) {
      return;
    }
    setCopySubmitting(true);
    setCopyError(null);
    try {
      const response = await api.post(`/api/agents/${id}/copy_to_tenant/`, {
        targetTenantId,
      });
      setCopyDialogOpen(false);
      setSuccess(
        response.data.agentReused
          ? t('pages:agents.copy.successReused', {
            targetTenantName: response.data.targetTenantName,
            name: response.data.agent?.name || formData.name,
          })
          : t('pages:agents.copy.successCreated', {
            targetTenantName: response.data.targetTenantName,
            name: response.data.agent?.name || formData.name,
          }),
      );
    } catch (err: unknown) {
      const apiError = err as { response?: { data?: { detail?: string; error?: string } } };
      setCopyError(
        apiError?.response?.data?.detail
          || apiError?.response?.data?.error
          || t('pages:agents.copy.error'),
      );
    } finally {
      setCopySubmitting(false);
    }
  }, [formData.name, id, t]);

  const getAgentTypeIcon = (type: string) => {
    switch (type) {
      default: return <Cloud />;
    }
  };

  const canSubmit = () => {
    if (!formData.name) return false;
    if (hasProtectedSystemVariables) return false;
    
    // All agent types require a provider
    if (!formData.aiAgentProviderId || !inferredAgentType) return false;
    
    if (!formData.providerAgentId) return false;
    if (!formData.direction) return false;
    
    return true;
  };

  if (loading) {
    return (
      <Box>
        <Skeleton variant="text" width={300} height={40} />
        <Skeleton variant="rectangular" height={400} sx={{ mt: 2, borderRadius: 1 }} />
      </Box>
    );
  }

  const headerTitle = isEditing
    ? formData.name || t('pages:agents.editTitle')
    : t('pages:agents.newTitle');

  const sectionIcon = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <PsychologyIcon sx={{ fontSize: 32, color: 'primary.main' }} />
      {selectedProvider && (
        <Chip
          icon={getAgentTypeIcon(inferredAgentType)}
          label={selectedProvider.providerTypeDisplay}
          color="primary"
          size="small"
        />
      )}
    </Box>
  );

  const breadcrumbs = [
    { label: t('common:menu.agents'), to: '/agents' },
    { label: isEditing ? t('pages:agents.edit') : t('pages:agents.new') },
  ];

  const headerActions = (
    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
      {canCopyResource && (
        <Button
          variant="outlined"
          startIcon={<ContentCopy />}
          onClick={() => {
            setCopyError(null);
            setCopyDialogOpen(true);
          }}
          disabled={saving || copySubmitting}
        >
          {t('pages:agents.copy.button')}
        </Button>
      )}
      <Button
        variant="contained"
        startIcon={
          saving ? <CircularProgress size={20} color="inherit" /> : <Save />
        }
        onClick={handleSubmit}
        disabled={saving || !canSubmit()}
      >
        {saving ? t('common:actions.saving') : t('common:actions.save')}
      </Button>
    </Box>
  );

  return (
    <Box>
      <DetailHeader
        title={headerTitle}
        sectionIcon={sectionIcon}
        entityId={agent?.id ?? null}
        breadcrumbs={breadcrumbs}
        actions={headerActions}
        backHref="/agents"
      />

      <TenantResourceCopyDialog
        open={copyDialogOpen}
        title={t('pages:agents.copy.title')}
        description={t('pages:agents.copy.description')}
        submitting={copySubmitting}
        error={copyError}
        sourceTenantId={user?.tenantId}
        onClose={() => {
          if (copySubmitting) {
            return;
          }
          setCopyDialogOpen(false);
          setCopyError(null);
        }}
        onSubmit={handleCopyToTenant}
      />

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Tabs - All tabs at the same level */}
      <Card>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2, pt: 2, pb: 1 }}>
          <ButtonTabs
            tabs={agentTabs}
            defaultTab="basic"
            tabParam="tab"
            useUrl
            renderContent={false}
          />
        </Box>

        <CardContent>
          {/* Basic Info Tab */}
          {normalizedTab === 'basic' && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, maxWidth: 800 }}>
              {/* Provider Selection - FIRST FIELD */}
              <FormControl fullWidth required disabled={isEditing || saving}>
                <InputLabel>{t('pages:agents.ai_agent_provider')}</InputLabel>
                <Select
                  value={formData.aiAgentProviderId}
                  label={t('pages:agents.ai_agent_provider')}
                  onChange={(e) => {
                    setFormData({
                      ...formData,
                      aiAgentProviderId: e.target.value,
                      providerAgentId: '',
                    });
                    setProviderAgentInputValue('');
                    setExternalConfig({});
                    selectTab('basic'); // Stay on basic tab when changing provider
                  }}
                >
                  {providers
                    .filter(p => p.supportsAgents)
                    .map((provider) => (
                      <MenuItem key={provider.id} value={provider.id}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {getAgentTypeIcon(provider.providerType)}
                          <span>{provider.name}</span>
                          <Typography variant="caption" color="text.secondary">
                            ({provider.providerTypeDisplay})
                          </Typography>
                        </Box>
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>

              {/* Name */}
              <TextField
                label={t('pages:agents.name')}
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                fullWidth
                required
                disabled={saving}
              />

              <FormControl fullWidth required disabled={saving}>
                <InputLabel>{t('pages:agents.direction')}</InputLabel>
                <Select
                  value={formData.direction}
                  label={t('pages:agents.direction')}
                  onChange={(event) => {
                    const direction = event.target.value as AgentDirection;
                    setFormData((previous) => ({
                      ...previous,
                      direction,
                      requiresPreCallScript: direction === 'inbound'
                        ? false
                        : previous.requiresPreCallScript,
                    }));
                  }}
                >
                  <MenuItem value="outbound">{t('pages:agents.directions.outbound')}</MenuItem>
                  <MenuItem value="inbound">{t('pages:agents.directions.inbound')}</MenuItem>
                </Select>
              </FormControl>

              {/* Description */}
              <TextField
                label={t('pages:agents.description')}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                fullWidth
                multiline
                rows={2}
                disabled={saving}
              />

              {selectedProvider && (
                <Autocomplete<ProviderAgentOption, false, false, true>
                  fullWidth
                  freeSolo
                  options={providerAgents}
                  getOptionLabel={(option) => 
                    typeof option === 'string' ? option : option.name
                  }
                  loading={loadingProviderAgents}
                  value={providerAgents.find(a => a.id === extractProviderAgentId(formData.providerAgentId)) || null}
                  inputValue={providerAgentInputValue}
                  onChange={(_, newValue) => {
                    if (typeof newValue === 'string') {
                      setFormData({ ...formData, providerAgentId: extractProviderAgentId(newValue) });
                      setProviderAgentInputValue(newValue);
                    } else if (newValue) {
                      setFormData({
                        ...formData,
                        providerAgentId: newValue.id,
                      });
                      setProviderAgentInputValue(newValue.name);
                    } else {
                      setFormData({ ...formData, providerAgentId: '' });
                      setProviderAgentInputValue('');
                    }
                  }}
                  onInputChange={(_, newInputValue, reason) => {
                    setProviderAgentInputValue(newInputValue);
                    if (reason === 'input') {
                      setFormData({ ...formData, providerAgentId: extractProviderAgentId(newInputValue) });
                    } else if (reason === 'clear') {
                      setFormData({ ...formData, providerAgentId: '' });
                    }
                  }}
                  disabled={saving}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('pages:agents.providerAgentId')}
                      required
                      placeholder={t('pages:agents.providerAgentIdPlaceholder')}
                      helperText={providerAgentsError || t('pages:agents.providerAgentIdHelper')}
                      error={!!providerAgentsError}
                    />
                  )}
                  noOptionsText={providerAgentsError || t('pages:agents.noProviderAgents')}
                />
              )}

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Checkbox
                    checked={formData.transferTargetRequired}
                    onChange={(event) => {
                      setFormData((prev) => ({
                        ...prev,
                        transferTargetRequired: event.target.checked,
                      }));
                    }}
                    disabled={saving}
                  />
                  <Typography variant="body2" fontWeight={500}>
                    {t('pages:agents.transferTargetRequired')}
                  </Typography>
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ pl: 4.5 }}>
                  {t('pages:agents.transferTargetRequiredHelp')}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Checkbox
                    checked={formData.requiresPreCallScript}
                    onChange={(event) => {
                      setFormData((previous) => ({
                        ...previous,
                        requiresPreCallScript: event.target.checked,
                      }));
                    }}
                    disabled={saving || formData.direction === 'inbound'}
                  />
                  <Typography variant="body2" fontWeight={500}>
                    {t('pages:agents.requiresPreCallScript')}
                  </Typography>
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ pl: 4.5 }}>
                  {formData.direction === 'inbound'
                    ? t('pages:agents.requiresPreCallScriptInboundHelp')
                    : t('pages:agents.requiresPreCallScriptHelp')}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Checkbox
                    checked={formData.appointmentCalendarRequired}
                    onChange={(event) => {
                      setFormData((prev) => ({
                        ...prev,
                        appointmentCalendarRequired: event.target.checked,
                      }));
                    }}
                    disabled={saving}
                  />
                  <Typography variant="body2" fontWeight={500}>
                    {t('pages:agents.appointmentCalendarRequired')}
                  </Typography>
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ pl: 4.5 }}>
                  {t('pages:agents.appointmentCalendarRequiredHelp')}
                </Typography>
              </Box>

            </Box>
          )}

          {normalizedTab === 'dynamicVariables' && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography variant="body2" color="text.secondary">
                {t('pages:agents.dynamicVariables.helpTooltip')}
              </Typography>

              <>
                {hasProtectedSystemVariables && (
                  <Alert severity="error">
                    {t('pages:agents.errors.protectedSystemVariables', { keys: protectedSystemVariableKeys.join(', ') })}
                  </Alert>
                )}
                {detectedVariables.length === 0 ? (
                  <Alert severity="info">{t('pages:agents.dynamicVariables.empty')}</Alert>
                ) : (
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 3,
                      borderColor: missingVariables.length > 0 ? 'warning.main' : 'success.main',
                      bgcolor: missingVariables.length > 0 ? 'warning.50' : 'success.50',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 2,
                    }}
                  >
                    <Chip
                      label={`${detectedVariables.length - missingVariables.length}/${detectedVariables.length}`}
                      color={missingVariables.length === 0 ? 'success' : 'warning'}
                      size="small"
                      sx={{ alignSelf: 'flex-start' }}
                    />

                    <Alert severity={missingVariables.length > 0 ? 'warning' : 'info'}>
                      {missingVariables.length > 0
                        ? t('pages:agents.dynamicVariables.helpTooltip')
                        : t('pages:agents.dynamicVariables.helpTooltip')}
                    </Alert>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {detectedVariables.map((variable) => {
                        const isMissing = missingVariables.includes(variable);
                        const isMarkedForRemoval = markedForRemovalKeySet.has(variable);
                        const displayVariable = variable
                          .replace(/^\{\{/, '')
                          .replace(/\}\}$/, '')
                          .trim();
                        return (
                          <Box
                            key={variable}
                            sx={{
                              display: 'grid',
                              gridTemplateColumns: { xs: '1fr', md: 'minmax(200px, 220px) 1fr auto' },
                              gap: 2,
                              alignItems: 'center',
                              opacity: isMarkedForRemoval ? 0.6 : 1,
                            }}
                          >
                            <Chip
                              label={displayVariable}
                              variant="outlined"
                              sx={{
                                fontFamily: 'monospace',
                                borderColor: isMissing ? 'warning.main' : 'success.main',
                                bgcolor: isMissing ? 'warning.100' : 'success.100',
                                borderRadius: 0.75,
                                minHeight: 40,
                                width: '100%',
                                justifyContent: 'flex-start',
                                px: 0.75,
                                '& .MuiChip-label': {
                                  width: '100%',
                                  textAlign: 'left',
                                  px: 1.75,
                                  py: 0.75,
                                },
                                '& .MuiChip-icon': {
                                  ml: 1,
                                  mr: 0.5,
                                },
                              }}
                              icon={
                                isMissing ? <Warning fontSize="small" /> : <CheckCircle fontSize="small" />
                              }
                              color={isMissing ? 'warning' : 'success'}
                            />
                            <TextField
                              size="small"
                              fullWidth
                              placeholder={t('pages:agents.dynamicVariables.valueLabel')}
                              value={dynamicVariables[variable]?.value || ''}
                              onChange={(e) => updateDynamicVariable(variable, e.target.value)}
                              disabled={saving || isMarkedForRemoval}
                              error={isMissing}
                            />
                            <FormControlLabel
                              control={(
                                <Checkbox
                                  checked={isMarkedForRemoval}
                                  onChange={(event) => toggleDynamicVariableMarkedForRemoval(variable, event.target.checked)}
                                  disabled={saving}
                                  color="error"
                                />
                              )}
                              label={t('pages:agents.dynamicVariables.removeOnSaveLabel')}
                              sx={{ ml: 0 }}
                            />
                          </Box>
                        );
                      })}
                    </Box>

                    {missingVariables.length === 0 && Object.keys(effectiveDynamicVariables).length > 0 && detectedVariables.length > 0 && (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                          {t('pages:agents.dynamicVariables.title')}
                        </Typography>
                        <Paper
                          sx={{
                            p: 2,
                            bgcolor: 'background.default',
                            border: '1px dashed',
                            borderColor: 'divider',
                          }}
                        >
                          <Typography variant="body2" fontStyle="italic" color="text.secondary">
                            "{substituteVariables(
                              Object.entries(effectiveDynamicVariableValues)
                                .map(([key, value]) => `${key}=${value}`)
                                .join(', '),
                              effectiveDynamicVariableValues,
                            )}"
                          </Typography>
                        </Paper>
                      </Box>
                    )}
                  </Paper>
                )}
              </>

              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  borderColor: 'divider',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                }}
              >
                <Typography variant="subtitle2">
                  {t('pages:agents.dynamicVariables.title')}
                </Typography>

                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: '240px 1fr 140px' },
                    gap: 2,
                    alignItems: 'end',
                  }}
                >
                  <TextField
                    label={t('pages:agents.dynamicVariables.keyLabel')}
                    value={newDynamicVariableKey}
                    onChange={(event) => setNewDynamicVariableKey(event.target.value)}
                    fullWidth
                    error={isNewKeyProtected || isNewKeyDuplicate}
                    helperText={
                      isNewKeyProtected
                        ? t('pages:agents.dynamicVariables.protectedPrefixHelper')
                        : isNewKeyDuplicate
                          ? t('pages:agents.dynamicVariables.duplicateKeyHelper')
                          : undefined
                    }
                  />
                  <TextField
                    label={t('pages:agents.dynamicVariables.valueLabel')}
                    value={newDynamicVariableValue}
                    onChange={(event) => setNewDynamicVariableValue(event.target.value)}
                    fullWidth
                  />
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleAddDynamicVariable}
                    disabled={!normalizedNewVariableKey || isNewKeyProtected || isNewKeyDuplicate}
                  >
                    {t('pages:agents.dynamicVariables.add')}
                  </Button>
                </Box>
              </Paper>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
